import { useTranslation } from "react-i18next";

import { Modal } from "../../../../shared/ui/modal";
import { useCenterSeats, useInviteMembers, CenterType } from "../../../center";
import { MENTOR_COLUMNS, readMentors } from "../../domain/csvImports";
import CsvImportPanel from "../../../../shared/ui/csv/CsvImportPanel";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    centerId: string;
    type: CenterType;
}

/** What the API answers when an address of the file is refused. */
const ERRORS: Record<string, string> = {
    already_a_member: "center.invitations.errorAlreadyMember",
    member_of_another_center: "center.invitations.errorOtherCenter",
    invitation_already_pending: "center.invitations.errorAlreadyInvited",
    no_seats_left: "center.invitations.seatsFull",
    invitation_email_invalid: "center.csv.apiErrors.invitation_email_invalid",
};

/**
 * Inviting a list of people from a spreadsheet with an `email` column.
 *
 * Everybody on it is invited as a practitioner — a mentor, in a training
 * centre. Making somebody an administrator stays a one-at-a-time decision.
 */
export default function InviteMembersCsvModal({ isOpen, onClose, centerId, type }: Props) {
    const { t, i18n } = useTranslation();
    const inviteMany = useInviteMembers(centerId);
    const { seats } = useCenterSeats(centerId);
    const training = type === "training";

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={t(training ? "center.csv.mentors.title" : "center.csv.mentors.titleTherapy")}
            className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            <h3 className="text-lg font-semibold text-content-primary">
                {t(training ? "center.csv.mentors.title" : "center.csv.mentors.titleTherapy")}
            </h3>
            <p className="mb-5 mt-1 text-sm text-content-tertiary">
                {t(training ? "center.csv.mentors.subtitle" : "center.csv.mentors.subtitleTherapy")}
            </p>
            <CsvImportPanel<string>
                columns={MENTOR_COLUMNS}
                read={readMentors}
                templateName={training ? "mentores" : "equipo"}
                templateRows={[["mentor1@escuela.es"], ["mentor2@escuela.es"]]}
                onImport={async (emails) =>
                    inviteMany.mutateAsync({
                        emails,
                        // The administrator's language: those invited may have no account yet.
                        language: i18n?.language?.startsWith("en") ? "en" : "es",
                    })
                }
                describe={(email) => email}
                errorMessage={(code) => t(ERRORS[code] ?? "center.invitations.errorGeneric")}
                submitLabel={(count) => t("center.csv.mentors.submit", { count })}
                // Each invitation holds a seat: the file is invited in order
                // until they run out, and the rest come back as "no seats".
                warning={(count) =>
                    seats && count > seats.available
                        ? t("center.csv.mentors.overSeats", { count, available: seats.available })
                        : null
                }
            />
        </Modal>
    );
}
